import { useHistory, useLocation } from 'react-router-dom';
import { isMonthValue } from 'src/utils/norwegianMonth';
import { MessageReadFilter } from './MessageReadFilterTabs';

const PAGE_FILTERS: readonly MessageReadFilter[] = [
    'all',
    'sentByMe',
    'sentToAll',
    'unread',
    'noStaffReply',
];

type Changes = { filter?: MessageReadFilter; month?: string | null };

/**
 * Message tab and month live in the URL so refresh, back and analytics drilldowns keep them.
 * Any change drops `from=analytics`, because the list no longer matches the dashboard figure.
 * The month is an administrator filter; others ignore it in the URL.
 */
export const useMessageListUrlFilters = ({
    adminFilters,
}: {
    adminFilters: boolean;
}) => {
    const location = useLocation();
    const history = useHistory();
    const params = new URLSearchParams(location.search);
    const rawFilter = params.get('filter') as MessageReadFilter | null;
    const rawMonth = params.get('month');
    const filter =
        rawFilter && PAGE_FILTERS.includes(rawFilter) ? rawFilter : 'all';
    const month =
        adminFilters && isMonthValue(rawMonth)
            ? (rawMonth as string)
            : undefined;

    const update = (changes: Changes) => {
        const next = new URLSearchParams(location.search);
        next.delete('from');
        if (changes.filter !== undefined) {
            if (changes.filter === 'all') next.delete('filter');
            else next.set('filter', changes.filter);
        }
        if (changes.month !== undefined) {
            if (changes.month) next.set('month', changes.month);
            else next.delete('month');
        }
        const search = next.toString();
        history.replace({ search: search ? `?${search}` : '' });
    };

    return {
        filter,
        month,
        adminFilters,
        fromAnalytics: adminFilters && params.get('from') === 'analytics',
        setFilter: (value: MessageReadFilter) => update({ filter: value }),
        setMonth: (value: string | null) => update({ month: value }),
        dismissAnalytics: () => update({}),
        clearAnalytics: () => update({ filter: 'all', month: null }),
    };
};
